/**
 * OneBase Órdenes de Compra
 * Página de gestión de órdenes de compra
 */

import React, { useState, useEffect } from 'react';
import {
  ordenCompraService,
  proyectoService,
  proveedorService,
} from '../services/onebaseService';
import { exportTableToPDF, formatCurrencyForPDF } from '../utils/pdfExport';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiDownload } from 'react-icons/fi';
import '../styles/onebase-ordenes.css';

import Loading from '../components/Loading';
const OneBaseOrdenes = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    proyecto: '',
    proveedor: '',
    fecha_emision: '',
    fecha_vencimiento: '',
    estado: 'pendiente',
    descripcion: '',
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [ordenesRes, proyectosRes, proveedoresRes] = await Promise.all([
        ordenCompraService.getAll({ status: 'activo' }),
        proyectoService.getAll({ status: 'activo' }),
        proveedorService.getAll({ status: 'activo' }),
      ]);

      // Procesar respuestas - manejar diferentes formatos de API
      const ordenesData = Array.isArray(ordenesRes.data) ? ordenesRes.data : (ordenesRes.data.results || []);
      const proyectosData = Array.isArray(proyectosRes.data) ? proyectosRes.data : (proyectosRes.data.results || []);
      const proveedoresData = Array.isArray(proveedoresRes.data) ? proveedoresRes.data : (proveedoresRes.data.results || []);

      setOrdenes(ordenesData);
      setProyectos(proyectosData);
      setProveedores(proveedoresData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedOrden) {
        await ordenCompraService.update(selectedOrden.id, formData);
        toast.success('Orden actualizada');
      } else {
        await ordenCompraService.create(formData);
        toast.success('Orden creada');
      }
      setShowForm(false);
      resetForm();
      cargarDatos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar orden');
    }
  };

  const handleEdit = (orden) => {
    setSelectedOrden(orden);
    setFormData({
      proyecto: orden.proyecto,
      proveedor: orden.proveedor || '',
      fecha_emision: orden.fecha_emision,
      fecha_vencimiento: orden.fecha_vencimiento || '',
      estado: orden.estado,
      descripcion: orden.descripcion || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta orden?')) {
      try {
        await ordenCompraService.delete(id);
        toast.success('Orden eliminada');
        cargarDatos();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      proyecto: '',
      proveedor: '',
      fecha_emision: '',
      fecha_vencimiento: '',
      estado: 'pendiente',
      descripcion: '',
    });
    setSelectedOrden(null);
  };

  const filteredOrdenes = ordenes.filter((o) =>
    o.numero_oc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.proyecto_nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  const estadoColors = {
    pendiente: '#f59e0b',
    emitida: '#3b82f6',
    recibida: '#69B32D',
    facturada: '#10b981',
    cancelada: '#6b7280',
  };

  const handleExportOrden = (orden) => {
    try {
      const data = [orden];
      const columns = [
        { key: 'numero_oc', label: 'Número OC' },
        { key: 'proyecto_nombre', label: 'Proyecto' },
        { key: 'proveedor_nombre', label: 'Proveedor' },
        { key: 'estado', label: 'Estado' },
        { key: 'total_oc', label: 'Total' },
      ];
      const filename = `orden_compra_${orden.numero_oc}_${new Date().toISOString().split('T')[0]}.pdf`;
      exportTableToPDF('Orden de Compra', data, columns, filename);
      toast.success('Orden exportada correctamente');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar orden');
    }
  };

  return (
    <div className="onebase-ordenes">
      <div className="page-header">
        <h1>Órdenes de Compra</h1>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FiPlus /> Nueva Orden
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Buscar por número de OC o proyecto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <Loading fullScreen={false} />
      ) : filteredOrdenes.length > 0 ? (
        <div className="ordenes-table">
          <table>
            <thead>
              <tr>
                <th>Número OC</th>
                <th>Proyecto</th>
                <th>Proveedor</th>
                <th>Fecha Emisión</th>
                <th>Estado</th>
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrdenes.map((orden) => (
                <tr key={orden.id}>
                  <td>
                    <strong>{orden.numero_oc}</strong>
                  </td>
                  <td>{orden.proyecto_nombre}</td>
                  <td>{orden.proveedor_nombre || '-'}</td>
                  <td>{new Date(orden.fecha_emision).toLocaleDateString('es-CL')}</td>
                  <td>
                    <span
                      className="estado-badge"
                      style={{ backgroundColor: estadoColors[orden.estado] }}
                    >
                      {orden.estado}
                    </span>
                  </td>
                  <td>{formatCurrency(orden.total_oc)}</td>
                  <td className="acciones">
                    <button
                      className="btn-icon"
                      onClick={() => handleExportOrden(orden)}
                      title="Exportar PDF"
                    >
                      <FiDownload />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(orden)}
                      title="Editar"
                    >
                      <FiEdit />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleDelete(orden.id)}
                      title="Eliminar"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No hay órdenes de compra</p>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            Crear primera orden
          </button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedOrden ? 'Editar' : 'Nueva'} Orden de Compra</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {selectedOrden ? (
                <div className="form-group">
                  <label>Número OC</label>
                  <input
                    type="text"
                    value={selectedOrden.numero_oc}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              ) : (
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #3b82f6',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <p style={{ color: '#1e40af', margin: 0, fontSize: '0.9rem' }}>
                    ℹ️ El número de OC se generará automáticamente al crear la orden
                  </p>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Proyecto *</label>
                  <select
                    name="proyecto"
                    value={formData.proyecto}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {proyectos.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Proveedor</label>
                  <select
                    name="proveedor"
                    value={formData.proveedor}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    {proveedores.map((pr) => (
                      <option key={pr.id} value={pr.id}>
                        {pr.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha Emisión *</label>
                  <input
                    type="date"
                    name="fecha_emision"
                    value={formData.fecha_emision}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Fecha Vencimiento</label>
                  <input
                    type="date"
                    name="fecha_vencimiento"
                    value={formData.fecha_vencimiento}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Estado</label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="emitida">Emitida</option>
                  <option value="recibida">Recibida</option>
                  <option value="facturada">Facturada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {selectedOrden ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OneBaseOrdenes;
